/**
 * Formats a number or string into a currency format with B/M suffix
 * @param value - The value to format (can be number or string)
 * @returns Formatted string (e.g., "$4.23B")
 */
export const formatMarketCap = (value: string | number): string => {
    const num = typeof value === 'string' ? parseFloat(value) : value;
    
    if (isNaN(num)) return '$0';
    if (num >= 1e12) {
        return `$${(num / 1e12).toFixed(2)}T`;
    } else if (num >= 1e9) {
        return `$${(num / 1e9).toFixed(2)}B`;
    } else if (num >= 1e6) {
        return `$${(num / 1e6).toFixed(2)}M`;
    } else if (num >= 1e3) {
        return `$${(num / 1e3).toFixed(2)}K`;
    }
    return `$${num.toFixed(2)}`;
};

/**
 * Calculates token utility level from a string value
 * @param value - Token utility value
 * @returns Number between 1 and 5
 */
export const getTokenUtilityLevel = (value: string): number => {
    const level = parseInt(value);
    if (isNaN(level) || level < 1) return 1;
    if (level > 5) return 5;
    return level;
};

/**
 * Gets the color class for autonomy level bars
 * @param level - Current level
 * @param barLevel - Bar's level
 * @returns Tailwind color class
 */
// export const getAutonomyLevelColor = (level: number, barLevel: number): string => {
//     if (level < barLevel) return 'bg-gray-800';
    
//     switch (barLevel) {
//         case 1: return 'bg-[#ef4444]';
//         case 2: return 'bg-[#f59e0b]';
//         case 3: return 'bg-[#22c55e]';
//         case 4: return 'bg-[#bf00ff]';
//         default: return 'bg-gray-800';
//     }
// };
export const getAutonomyLevelColor = (currentLevel: number, barLevel: number): string => {
    // Only color the bar that matches the current level
    if (currentLevel === barLevel) {
        // Return specific colors based on the level
        switch (barLevel) {
            case 1: return 'bg-[#ef4444]'; // Red for Low
            case 2: return 'bg-[#f59e0b]'; // Orange for Medium
            case 3: return 'bg-[#22c55e]'; // Green for High
            case 4: return 'bg-[#bf00ff]'; // Purple for Full
            default: return 'bg-gray-800'; // Default gray
        }
    }
    
    // All other bars should be gray
    return 'bg-gray-800';
};
/**
 * Gets the height class for autonomy level bars
 * @param barLevel - Bar's level
 * @returns Tailwind height class
 */
export const getAutonomyBarHeight = (barLevel: number): string => {
    switch (barLevel) {
        case 1: return 'h-16'; // 25%
        case 2: return 'h-24'; // 50%
        case 3: return 'h-32'; // 75%
        case 4: return 'h-40'; // 100%
        default: return 'h-16';
    }
};

/**
 * Maps autonomy level string to corresponding number
 * @param levelString - Autonomy level string from API
 * @returns number (1-4) representing the level
 */
export const mapAutonomyLevel = (levelString: string): number => {
    const levelMap: { [key: string]: number } = {
        'LOW': 1,
        'MEDIUM': 2,
        'HIGH': 3,
        'FULL': 4
    };

    // Convert to uppercase to ensure case-insensitive matching
    const normalizedLevel = levelString?.toUpperCase() || 'LOW';
    return levelMap[normalizedLevel] || 1; // Default to 1 (LOW) if not found
};

export const formatTimeAgo = (date: string): string => {
    const now = new Date();
    const past = new Date(date);
    const diffTime = Math.abs(now.getTime() - past.getTime());
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
    const diffMinutes = Math.floor(diffTime / (1000 * 60));

    if (diffDays > 0) {
        return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
    } else if (diffHours > 0) {
        return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
    } else if (diffMinutes > 0) {
        return `${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`;
    } else {
        return 'just now';
    }
}; 